import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vShow as _vShow, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers, unref as _unref, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText } from "vue"

const _hoisted_1 = {
  key: 0,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
}
const _hoisted_2 = {
  key: 1,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2.5",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
}
const _hoisted_3 = {
  key: 2,
  class: "cw-fab-badge"
}
const _hoisted_4 = { class: "cw-header" }
const _hoisted_5 = { class: "cw-hleft" }
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 0,
  class: "cw-chips"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 1,
  class: "cw-qgrid cw-qgrid--top"
}
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "cw-qi" }
const _hoisted_12 = {
  key: 0,
  class: "cw-welcome"
}
const _hoisted_13 = {
  key: 0,
  class: "cw-qgrid"
}
const _hoisted_14 = ["src"]
const _hoisted_15 = ["src", "alt"]
const _hoisted_16 = {
  key: 1,
  class: "cw-av-ini"
}
const _hoisted_17 = { class: "cw-col" }
const _hoisted_18 = ["innerHTML"]
const _hoisted_19 = { class: "cw-time" }
const _hoisted_20 = {
  key: 1,
  class: "cw-row bot"
}
const _hoisted_21 = { class: "cw-av bot" }
const _hoisted_22 = ["src"]
const _hoisted_23 = { class: "cw-foot" }
const _hoisted_24 = ["disabled"]

import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { avatarColor, initials } from '@/composables/useHelpers'
import { N8N_CHAT_WIDGET_URL } from '@/api/n8n'
import { useLogo } from '@/composables/useLogo'
import { activeConvContext } from '@/composables/useChatTrigger'
import { useAdmin } from '@/composables/useAdmin'

const EMPRESA_CLIENTE_FLUXO_ID = 2

interface Msg { role: 'bot' | 'user'; text: string; time: string }

const MIN_W = 280, MAX_W = 620
const MIN_H = 360, MAX_H = 780

const CNPJ_CACHE_PREFIX = 'beto_cnpj_v5_'
const FAILURE_COOLDOWN_MS = 60_000
type ConsultCacheEntry =
  | { kind: 'result'; text: string; name: string | null }
  | { kind: 'failure'; at: number }
interface ClienteAction { icon: string; label: string; prompt: string }

export default /*@__PURE__*/_defineComponent({
  __name: 'ChatWidget',
  props: {
    sidebarCollapsed: { type: Boolean }
  },
  setup(__props: any) {

const { logoUrl } = useLogo()
const WEBHOOK = N8N_CHAT_WIDGET_URL

const CHIPS = [
  { label: '📤 Novo aprendiz',  q: 'Quero cadastrar um novo aprendiz' },
  { label: '⚖️ Direitos CLT',   q: 'Quais são os direitos do jovem aprendiz pela CLT?' },
  { label: '📊 Frequência',     q: 'Gerar relatório de frequência do mês' },
  { label: '📍 Unidades SP',    q: 'Quais unidades do ISBET existem em SP?' },
]

const store   = useStore()
const authUser = computed(() => store.getters.authUser)
const userInitials  = computed(() => initials(authUser.value?.name || 'U'))
const userAvatarBg  = computed(() => avatarColor(authUser.value?.name || 'U'))
const userAvatarUrl = computed(() => authUser.value?.avatar_url || '')

const props = __props

const fabVisible = ref(false)
const isOpen   = ref(false)
const messages = ref<Msg[]>([])
const typing   = ref(false)
const busy     = ref(false)
const text     = ref('')
const focused  = ref(false)
const unread   = ref(0)
const msgsEl   = ref<HTMLElement | null>(null)
const inputEl  = ref<HTMLTextAreaElement | null>(null)

const panelW = ref(355)
const panelH = ref(530)
const { globalSettings } = useAdmin()
const aiEnabled = computed(() => globalSettings.value.betoAiEnabled)

const fabLeft = computed(() => props.sidebarCollapsed ? '80px' : '236px')

// ── Consulta automática de CNPJ (fluxo 2 · Empresa Cliente) ──────────────
// Ver docs/planos/PILOTO-BETO-CNPJ.md: ao abrir uma conversa de Empresa Cliente com CNPJ
// preenchido, o Beto consulta sozinho em segundo plano (via MCP, do lado do n8n, painel fechado,
// sem indicador de digitação visível). Quando resolve, mostra o balão de fala com o nome da
// empresa encontrada — só ao clicar o agente vê o resumo completo no painel. Resultado (sucesso
// ou falha) fica cacheado por conversation_id nesta sessão do navegador, pra não repetir a
// consulta ao reabrir a mesma conversa.
// v3: cache passou a guardar { text, name } (JSON) em vez de só o texto — nome extraído da
// resposta pra personalizar o balão antes de abrir o painel.
// v4: timeout de 6s pra 25s (era curto demais pro AI Agent + MCP responderem, causava 'failed'
// falso-negativo) — muda o prefixo pra não manter presa uma tentativa que falhou só por timeout.
// v5: falha não fica mais presa pro resto da sessão — vira um "esfriamento" curto (1min); depois
// disso reabrir a conversa tenta de novo sozinho, em vez de exigir fechar a aba.
const autoConsultCache = ref<Record<number, string>>({})
const queryingConvIds = new Set<number>()
const pendingConvId = ref<number | null>(null)
let lastHandledConvId: number | null = null

function cnpjCacheKey(convId: number) { return CNPJ_CACHE_PREFIX + convId }

function getCachedConsult(convId: number): string | null {
  if (convId in autoConsultCache.value) return autoConsultCache.value[convId]
  const stored = sessionStorage.getItem(cnpjCacheKey(convId))
  if (stored !== null) autoConsultCache.value[convId] = stored
  return stored
}

function setCachedConsult(convId: number, value: string) {
  autoConsultCache.value[convId] = value
  sessionStorage.setItem(cnpjCacheKey(convId), value)
}

function decodeCacheEntry(raw: string): ConsultCacheEntry | null {
  try {
    const p = JSON.parse(raw)
    if (p?.kind === 'result' && typeof p.text === 'string') {
      return { kind: 'result', text: p.text, name: typeof p.name === 'string' ? p.name : null }
    }
    if (p?.kind === 'failure' && typeof p.at === 'number') return { kind: 'failure', at: p.at }
    return null
  } catch {
    return null
  }
}

function cachedEntryFor(convId: number): ConsultCacheEntry | null {
  const raw = getCachedConsult(convId)
  return raw ? decodeCacheEntry(raw) : null
}

const activeConvId     = computed(() => activeConvContext.value?.id ?? null)
const isEmpresaCliente = computed(() => activeConvContext.value?.fluxoId === EMPRESA_CLIENTE_FLUXO_ID)
const autoConsultResult = computed<{ text: string; name: string | null } | null>(() => {
  const id = activeConvId.value
  if (id == null) return null
  const entry = cachedEntryFor(id)
  return entry?.kind === 'result' ? { text: entry.text, name: entry.name } : null
})
const autoResolvedOk = computed(() => !!autoConsultResult.value)
const isConsultPending = computed(() => activeConvId.value != null && pendingConvId.value === activeConvId.value)

// Balão genérico (fallback): fluxo 2, consulta ainda não resolveu com sucesso, e não está em
// andamento agora — cobre "sem CNPJ", "ainda não tentada" (fora da janela silenciosa) e "falhou".
const helpHintActive = computed(() => aiEnabled.value && isEmpresaCliente.value && !autoResolvedOk.value && !isConsultPending.value)
// Balão personalizado: consulta já trouxe o nome da empresa, painel ainda fechado.
const nameHintActive = computed(() => aiEnabled.value && isEmpresaCliente.value && autoResolvedOk.value)
const nameHintText = computed(() => {
  const name = autoConsultResult.value?.name
  return name ? `Quer ajuda com ${name}?` : 'Encontrei os dados desse cliente — quer ajuda?'
})
const balloonActive = computed(() => helpHintActive.value || nameHintActive.value)
const balloonText   = computed(() => nameHintActive.value ? nameHintText.value : 'Precisa de Ajuda com a empresa? Pode falar comigo.')

// Ações rápidas do fluxo 2 (Empresa Cliente): fixas no topo do painel assim que a conversa tem
// CNPJ, oferecendo consultas específicas sobre esse mesmo CNPJ — não esperam o resumo automático
// resolver, então o agente pode usá-las mesmo antes ou se a consulta automática falhar.
const clienteActions = computed<ClienteAction[]>(() => {
  const cnpj = activeConvContext.value?.cnpj
  if (!cnpj) return []
  const masked = fmtCnpjMask(cnpj)
  return [
    { icon: '💰', label: 'Débito Financeiro',      prompt: `Consulte o CNPJ ${masked} e me traga a situação de débito financeiro (valores em aberto, inadimplência, se houver) desse cliente.` },
    { icon: '🎓', label: 'Informações de Jovens',  prompt: `Consulte o CNPJ ${masked} e me traga as informações dos jovens aprendizes/estagiários vinculados a esse cliente (quantidade, nomes, status).` },
    { icon: '📇', label: 'Contatos',                prompt: `Consulte o CNPJ ${masked} e me traga os contatos (telefone, e-mail, responsável) desse cliente.` },
    { icon: '📄', label: 'Contratos',                prompt: `Consulte o CNPJ ${masked} e me traga os contratos vigentes desse cliente.` },
  ]
})

function fmtCnpjMask(cnpj: string): string {
  const d = cnpj.replace(/\D/g, '')
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : cnpj
}

// Primeiro trecho em **negrito** da resposta — é onde o Beto já costuma destacar o nome da
// empresa (reforçado pela instrução abaixo). Sem parsing rígido: se não achar, o balão cai pro
// texto genérico em vez de quebrar.
function extractCompanyName(text: string): string | null {
  const m = text.match(/\*\*(.+?)\*\*/)
  return m ? m[1].trim() : null
}

// O workflow n8n hoje só lê `mensagem` como texto livre — por isso o pedido de consulta automática
// vai como uma instrução em português, igual a um agente teria digitado, em vez de um campo
// estruturado que o AI Agent ainda não sabe ler (ver docs/planos/PILOTO-BETO-CNPJ.md).
function buildAutoConsultPrompt(cnpj: string): string {
  return `Uma nova conversa de Empresa Cliente foi aberta. Consulte o CNPJ ${fmtCnpjMask(cnpj)} e comece a resposta destacando em **negrito** o nome da empresa. Depois traga um resumo básico (nome e o que encontrar) e pergunte se eu preciso de ajuda com esse cliente.`
}

async function autoQueryCnpj(convId: number, cnpj: string) {
  if (queryingConvIds.has(convId)) {
    console.info('[Beto] consulta automática já em andamento pra essa conversa, ignorando novo disparo', { convId })
    return
  }
  queryingConvIds.add(convId)
  pendingConvId.value = convId
  console.info('[Beto] disparando consulta automática de CNPJ', { convId, cnpj, webhook: WEBHOOK })

  try {
    const controller = new AbortController()
    // Painel fica fechado durante a espera (v3) — não há mais spinner visível pra evitar deixar
    // "pensando", então o timeout pode ser generoso o bastante pro AI Agent + ferramenta MCP
    // resolverem com folga, em vez dos 6s de quando isso abria o painel na hora.
    const timeoutId = setTimeout(() => controller.abort(), 25000)
    let res: Response
    try {
      res = await fetch(WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // mensagem carrega o pedido em texto — o mesmo campo que o envio manual já usa, então o
        // workflow n8n atual entende isso sem precisar de nenhum ajuste (ver docs/planos/PILOTO-BETO-CNPJ.md).
        // conversa vai junto como contexto extra, ignorável hoje, pra quando o workflow quiser lê-lo direto.
        body: JSON.stringify({ mensagem: buildAutoConsultPrompt(cnpj), conversa: { id: convId, fluxo_id: EMPRESA_CLIENTE_FLUXO_ID, cnpj } }),
        signal: controller.signal,
      })
    } finally {
      clearTimeout(timeoutId)
    }
    console.info('[Beto] resposta da consulta automática recebida', { convId, status: res.status, ok: res.ok })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    const output =
      (Array.isArray(data) ? data[0]?.output  : data?.output)  ||
      (Array.isArray(data) ? data[0]?.text    : data?.text)    ||
      (Array.isArray(data) ? data[0]?.message : data?.message)
    if (!output) throw new Error(`resposta sem output/text/message reconhecível: ${JSON.stringify(data).slice(0, 300)}`)
    setCachedConsult(convId, JSON.stringify({ kind: 'result', text: output, name: extractCompanyName(output) } satisfies ConsultCacheEntry))
    console.info('[Beto] consulta automática de CNPJ concluída com sucesso', { convId, name: extractCompanyName(output) })
    // Painel continua fechado — a mensagem já entra na conversa (pro badge/histórico) e o balão
    // com o nome da empresa é quem convida o agente a abrir o painel e ver o resumo.
    addMsgOnce(output)
  } catch (err) {
    console.warn('[Beto] consulta automática de CNPJ falhou', { convId, err })
    setCachedConsult(convId, JSON.stringify({ kind: 'failure', at: Date.now() } satisfies ConsultCacheEntry))
  } finally {
    if (pendingConvId.value === convId) pendingConvId.value = null
    queryingConvIds.delete(convId)
  }
}

watch(activeConvContext, (ctx) => {
  const id = ctx?.id ?? null
  if (id === lastHandledConvId) return
  lastHandledConvId = id
  if (!ctx) return
  if (!aiEnabled.value) { console.info('[Beto] Beto desligado (betoAiEnabled) — não dispara consulta automática'); return }
  if (ctx.fluxoId !== EMPRESA_CLIENTE_FLUXO_ID) { console.info('[Beto] conversa não é fluxo 2 (Empresa Cliente) — não dispara', { fluxoId: ctx.fluxoId }); return }
  if (!ctx.cnpj) { console.info('[Beto] conversa fluxo 2 sem CNPJ em custom_attributes — não dispara', { convId: ctx.id }); return }

  // Sucesso já cacheado: o balão já reflete isso via computed (autoConsultResult), mas a mensagem
  // em si (messages) é só em memória — some num reload/remount mesmo com o sessionStorage intacto.
  // addMsgOnce garante repor sem duplicar se ela já estiver lá.
  // Falha cacheada: só não repete enquanto ainda estiver "esfriando" (FAILURE_COOLDOWN_MS); depois
  // disso, reabrir a conversa tenta de novo sozinho.
  const entry = cachedEntryFor(ctx.id)
  if (entry?.kind === 'result') {
    console.info('[Beto] já tinha resultado em cache pra essa conversa, repondo na lista de mensagens', { convId: ctx.id })
    addMsgOnce(entry.text)
    return
  }
  if (entry?.kind === 'failure' && Date.now() - entry.at < FAILURE_COOLDOWN_MS) {
    console.info('[Beto] falha recente em cooldown, não retenta ainda', { convId: ctx.id, faltamMs: FAILURE_COOLDOWN_MS - (Date.now() - entry.at) })
    return
  }
  autoQueryCnpj(ctx.id, ctx.cnpj)
}, { immediate: true })

const fabStyle   = computed(() => ({ left: fabLeft.value }))
const panelStyle = computed(() => ({
  left:   fabLeft.value,
  width:  panelW.value + 'px',
  height: panelH.value + 'px',
}))

let _rzDir: 'top' | 'right' | 'corner' | null = null
let _rzX = 0, _rzY = 0, _rzW = 0, _rzH = 0

function startResize(e: MouseEvent, dir: 'top' | 'right' | 'corner') {
  _rzDir = dir; _rzX = e.clientX; _rzY = e.clientY
  _rzW = panelW.value; _rzH = panelH.value
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
}
function onResize(e: MouseEvent) {
  if (!_rzDir) return
  const dx = e.clientX - _rzX
  const dy = e.clientY - _rzY
  if (_rzDir === 'top' || _rzDir === 'corner')
    panelH.value = Math.min(MAX_H, Math.max(MIN_H, _rzH - dy))
  if (_rzDir === 'right' || _rzDir === 'corner')
    panelW.value = Math.min(MAX_W, Math.max(MIN_W, _rzW + dx))
}
function stopResize() {
  _rzDir = null
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
}

function toggle() {
  if (!aiEnabled.value) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    unread.value = 0
    nextTick(() => inputEl.value?.focus())
  }
}

watch(aiEnabled, (enabled) => {
  if (!enabled) { isOpen.value = false; fabVisible.value = false }
})

function ts() {
  return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function scrollBottom() {
  nextTick(() => { if (msgsEl.value) msgsEl.value.scrollTop = msgsEl.value.scrollHeight })
}

function addMsg(role: 'bot' | 'user', txt: string) {
  messages.value.push({ role, text: txt, time: ts() })
  if (role === 'bot' && !isOpen.value) unread.value++
  scrollBottom()
}

// Usado pela consulta automática de CNPJ: o ciclo de auto-atualização da fila recalcula a conversa
// ativa periodicamente (às vezes passando por null antes de resolver de novo), o que pode disparar
// o mesmo resultado cacheado mais de uma vez. Em vez de tentar blindar cada re-disparo, garante que
// o mesmo texto nunca aparece duas vezes no histórico da sessão.
function addMsgOnce(txt: string) {
  if (messages.value.some(m => m.text === txt)) return
  addMsg('bot', txt)
}

async function send() {
  if (busy.value || !text.value.trim()) return
  const msg = text.value.trim()
  text.value = ''
  if (inputEl.value) inputEl.value.style.height = 'auto'
  busy.value = true

  addMsg('user', msg)
  typing.value = true
  scrollBottom()

  try {
    const res = await fetch(WEBHOOK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mensagem: msg }),
    })
    typing.value = false
    if (!res.ok) {
      addMsg('bot', `⚠️ Erro do servidor: ${res.status} ${res.statusText}`)
    } else {
      const data = await res.json()
      const output =
        (Array.isArray(data) ? data[0]?.output  : data?.output)  ||
        (Array.isArray(data) ? data[0]?.text    : data?.text)    ||
        (Array.isArray(data) ? data[0]?.message : data?.message) ||
        JSON.stringify(data)
      addMsg('bot', output)
    }
  } catch {
    typing.value = false
    addMsg('bot', '⚠️ Não foi possível conectar ao servidor. Verifique se o n8n está rodando.')
  }

  busy.value = false
  nextTick(() => inputEl.value?.focus())
}

function quickSend(q: string) { text.value = q; send() }
function onKey(e: KeyboardEvent) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }
function autoResize(e: Event) {
  const el = e.target as HTMLTextAreaElement
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 100) + 'px'
}

function handleShortcut(e: KeyboardEvent) {
  if (e.ctrlKey && e.shiftKey && (e.key === '*' || e.key === '8')) {
    e.preventDefault()
    if (!aiEnabled.value) return
    fabVisible.value = !fabVisible.value
    if (!fabVisible.value) isOpen.value = false
  }
}

function escHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function md2h(s: string) {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^#{1,3} (.+)$/gm, '<strong>$1</strong>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>[\s\S]*?<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
    .replace(/\n\n/g, '<br><br>')
    .replace(/\n/g, '<br>')
}

onMounted(() => window.addEventListener('keydown', handleShortcut))
onUnmounted(() => {
  window.removeEventListener('keydown', handleShortcut)
  stopResize()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("button", {
      class: _normalizeClass(["cw-fab", { 'cw-fab--open': isOpen.value, 'cw-fab--hint': balloonActive.value }]),
      style: _normalizeStyle(fabStyle.value),
      onClick: toggle,
      title: "Beto - Assistente Digital"
    }, [
      (!isOpen.value)
        ? (_openBlock(), _createElementBlock("svg", _hoisted_1, [...(_cache[11] || (_cache[11] = [
            _createElementVNode("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" }, null, -1)
          ]))]))
        : (_openBlock(), _createElementBlock("svg", _hoisted_2, [...(_cache[12] || (_cache[12] = [
            _createElementVNode("line", {
              x1: "18",
              y1: "6",
              x2: "6",
              y2: "18"
            }, null, -1),
            _createElementVNode("line", {
              x1: "6",
              y1: "6",
              x2: "18",
              y2: "18"
            }, null, -1)
          ]))])),
      (unread.value > 0 && !isOpen.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(unread.value), 1))
        : _createCommentVNode("", true)
    ], 6), [
      [_vShow, aiEnabled.value && (fabVisible.value || balloonActive.value || isOpen.value)]
    ]),
    _createVNode(_Transition, { name: "cw-balloon" }, {
      default: _withCtx(() => [
        (balloonActive.value && !isOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "cw-hint-balloon",
              style: _normalizeStyle(fabStyle.value),
              onClick: toggle
            }, _toDisplayString(balloonText.value), 5))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, { name: "cw" }, {
      default: _withCtx(() => [
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "cw-panel",
              style: _normalizeStyle(panelStyle.value)
            }, [
              _createElementVNode("div", {
                class: "cw-rz cw-rz-top",
                onMousedown: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (startResize($event, 'top')), ["prevent"]))
              }, null, 32),
              _createElementVNode("div", {
                class: "cw-rz cw-rz-right",
                onMousedown: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (startResize($event, 'right')), ["prevent"]))
              }, null, 32),
              _createElementVNode("div", {
                class: "cw-rz cw-rz-corner",
                onMousedown: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (startResize($event, 'corner')), ["prevent"]))
              }, [...(_cache[13] || (_cache[13] = [
                _createElementVNode("svg", { viewBox: "0 0 10 10" }, [
                  _createElementVNode("path", {
                    d: "M9 1 L1 9 M9 5 L5 9 M9 9 L9 9",
                    stroke: "currentColor",
                    "stroke-width": "1.5",
                    "stroke-linecap": "round"
                  })
                ], -1)
              ]))], 32),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("img", {
                    src: _unref(logoUrl),
                    alt: "ISBET",
                    class: "cw-logo-img"
                  }, null, 8, _hoisted_6),
                  _cache[14] || (_cache[14] = _createElementVNode("div", { class: "cw-vline" }, null, -1)),
                  _cache[15] || (_cache[15] = _createElementVNode("div", null, [
                    _createElementVNode("div", { class: "cw-htitle" }, "Beto - Assistente Digital"),
                    _createElementVNode("div", { class: "cw-hsub" }, "Jovem Aprendiz & Estágio")
                  ], -1))
                ]),
                _cache[16] || (_cache[16] = _createElementVNode("div", { class: "cw-hstatus" }, [
                  _createElementVNode("span", { class: "cw-dot" }),
                  _createTextVNode("Online")
                ], -1))
              ]),
              _cache[27] || (_cache[27] = _createElementVNode("div", { class: "cw-stripe" }, null, -1)),
              (messages.value.length === 0 && !isEmpresaCliente.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(CHIPS, (c) => {
                      return _createElementVNode("button", {
                        key: c.q,
                        class: "cw-chip",
                        onClick: ($event: any) => (quickSend(c.q))
                      }, _toDisplayString(c.label), 9, _hoisted_8)
                    }), 64))
                  ]))
                : _createCommentVNode("", true),
              (isEmpresaCliente.value && clienteActions.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(clienteActions.value, (a) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: a.label,
                        class: "cw-qbtn",
                        onClick: ($event: any) => (quickSend(a.prompt))
                      }, [
                        _createElementVNode("span", _hoisted_11, _toDisplayString(a.icon), 1),
                        _createTextVNode(_toDisplayString(a.label), 1)
                      ], 8, _hoisted_10))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", {
                class: "cw-msgs",
                ref_key: "msgsEl",
                ref: msgsEl
              }, [
                (messages.value.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      _cache[21] || (_cache[21] = _createElementVNode("div", { class: "cw-w-icon" }, [
                        _createElementVNode("svg", {
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "1.5",
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round"
                        }, [
                          _createElementVNode("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" })
                        ])
                      ], -1)),
                      _cache[22] || (_cache[22] = _createElementVNode("h3", null, "Olá! Como posso ajudar?", -1)),
                      _cache[23] || (_cache[23] = _createElementVNode("p", null, "Sou o assistente do ISBET — cadastros, direitos trabalhistas, relatórios e muito mais.", -1)),
                      (!isEmpresaCliente.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            _createElementVNode("button", {
                              class: "cw-qbtn",
                              onClick: _cache[3] || (_cache[3] = ($event: any) => (quickSend('Quero cadastrar um novo aprendiz')))
                            }, [...(_cache[17] || (_cache[17] = [
                              _createElementVNode("span", { class: "cw-qi" }, "📤", -1),
                              _createTextVNode("Novo aprendiz ", -1)
                            ]))]),
                            _createElementVNode("button", {
                              class: "cw-qbtn",
                              onClick: _cache[4] || (_cache[4] = ($event: any) => (quickSend('Quais são os direitos do jovem aprendiz pela CLT?')))
                            }, [...(_cache[18] || (_cache[18] = [
                              _createElementVNode("span", { class: "cw-qi" }, "⚖️", -1),
                              _createTextVNode("Direitos CLT ", -1)
                            ]))]),
                            _createElementVNode("button", {
                              class: "cw-qbtn",
                              onClick: _cache[5] || (_cache[5] = ($event: any) => (quickSend('Quais unidades do ISBET existem em SP?')))
                            }, [...(_cache[19] || (_cache[19] = [
                              _createElementVNode("span", { class: "cw-qi" }, "📍", -1),
                              _createTextVNode("Unidades ISBET ", -1)
                            ]))]),
                            _createElementVNode("button", {
                              class: "cw-qbtn",
                              onClick: _cache[6] || (_cache[6] = ($event: any) => (quickSend('Como funciona o programa de aprendizagem do ISBET?')))
                            }, [...(_cache[20] || (_cache[20] = [
                              _createElementVNode("span", { class: "cw-qi" }, "📋", -1),
                              _createTextVNode("Programa ISBET ", -1)
                            ]))])
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages.value, (m, i) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: i,
                    class: _normalizeClass(["cw-row", m.role])
                  }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["cw-av", m.role]),
                      style: _normalizeStyle(m.role === 'user' ? { background: userAvatarBg.value } : {})
                    }, [
                      (m.role === 'bot')
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            src: _unref(logoUrl),
                            alt: "Beto",
                            class: "cw-av-logo"
                          }, null, 8, _hoisted_14))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            (userAvatarUrl.value)
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  src: userAvatarUrl.value,
                                  alt: authUser.value?.name,
                                  class: "cw-av-photo",
                                  onError: _cache[7] || (_cache[7] = (e) => (e.target as HTMLImageElement).style.display='none')
                                }, null, 40, _hoisted_15))
                              : (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(userInitials.value), 1))
                          ], 64))
                    ], 6),
                    _createElementVNode("div", _hoisted_17, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["cw-bub", m.role]),
                        innerHTML: m.role === 'bot' ? md2h(m.text) : escHtml(m.text)
                      }, null, 10, _hoisted_18),
                      _createElementVNode("div", _hoisted_19, _toDisplayString(m.time), 1)
                    ])
                  ], 2))
                }), 128)),
                (typing.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                      _createElementVNode("div", _hoisted_21, [
                        _createElementVNode("img", {
                          src: _unref(logoUrl),
                          alt: "Beto",
                          class: "cw-av-logo"
                        }, null, 8, _hoisted_22)
                      ]),
                      _cache[24] || (_cache[24] = _createElementVNode("div", { class: "cw-dots" }, [
                        _createElementVNode("span"),
                        _createElementVNode("span"),
                        _createElementVNode("span")
                      ], -1))
                    ]))
                  : _createCommentVNode("", true)
              ], 512),
              _createElementVNode("div", _hoisted_23, [
                _createElementVNode("div", {
                  class: _normalizeClass(["cw-irow", { 'cw-irow--focus': focused.value }])
                }, [
                  _withDirectives(_createElementVNode("textarea", {
                    ref_key: "inputEl",
                    ref: inputEl,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((text).value = $event)),
                    rows: "1",
                    placeholder: "Digite sua dúvida ou solicitação...",
                    onKeydown: onKey,
                    onInput: autoResize,
                    onFocus: _cache[9] || (_cache[9] = ($event: any) => (focused.value = true)),
                    onBlur: _cache[10] || (_cache[10] = ($event: any) => (focused.value = false))
                  }, null, 544), [
                    [_vModelText, text.value]
                  ]),
                  _createElementVNode("button", {
                    class: "cw-send",
                    disabled: busy.value || !text.value.trim(),
                    onClick: send
                  }, [...(_cache[25] || (_cache[25] = [
                    _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "2.5",
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round"
                    }, [
                      _createElementVNode("path", { d: "M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z" })
                    ], -1)
                  ]))], 8, _hoisted_24)
                ], 2),
                _cache[26] || (_cache[26] = _createElementVNode("div", { class: "cw-hint" }, [
                  _createTextVNode("Assistente "),
                  _createElementVNode("strong", null, "ISBET")
                ], -1))
              ])
            ], 4))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})